import React, { memo, useCallback, useEffect, useState } from 'react'

import './DemoForm.scss'

import { useAuth } from '../../contexts/AuthContext'
import { type Demo } from '../../Root'
import { LabeledField } from '../LabeledField'
import { DemoFormFields } from './DemoFormFields'
import {
  type DateRangeDatePickerModes,
  type DemoFormValues,
} from './types'

export interface EditDemoFormProps {
  demo: Demo
  onCancel: () => void
  onSaved: () => Promise<void>
}

const demoToFormValues = (demo: Demo): DemoFormValues => ({
  brandColorHex: demo.brandColorHex,
  brandColorLightHex: demo.brandColorLightHex,
  brandColorDarkHex: demo.brandColorDarkHex,
  brandColorTextHex: demo.brandColorTextHex,
  brandLogoUrl: demo.brandLogoUrl,
  businessId: demo.businessId ?? '',
  bookkeepingOn: demo.bookkeepingOn,
  showDescriptions: demo.showDescriptions,
  showReceiptUploads: demo.showReceiptUploads,
  showInvoicesTab: demo.showInvoicesTab,
  showCustomerVendor: demo.showCustomerVendor,
  datePickerMode:
    (demo.datePickerMode as DateRangeDatePickerModes | undefined) ?? null,
  layerEnvironmentOverride: demo.layerEnvironmentOverride ?? null,
  defaultToMobile: demo.defaultToMobile,
})

export const EditDemoForm = memo(function EditDemoForm({
  demo,
  onCancel,
  onSaved,
}: EditDemoFormProps) {
  const [values, setValues] = useState<DemoFormValues>(() => demoToFormValues(demo))
  const [password, setPassword] = useState('')
  const [layerClientId, setLayerClientId] = useState('')
  const [layerClientSecret, setLayerClientSecret] = useState('')
  const [clearCredentialOverride, setClearCredentialOverride] = useState(false)
  const { authFetch } = useAuth()

  useEffect(() => {
    setValues(demoToFormValues(demo))
    setPassword('')
    setLayerClientId('')
    setLayerClientSecret('')
    setClearCredentialOverride(false)
  }, [demo])

  const handleChange = useCallback(
    <K extends keyof DemoFormValues>(key: K, value: DemoFormValues[K]) => {
      setValues(prev => ({ ...prev, [key]: value }))
    },
    [],
  )

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      try {
        const response = await authFetch(`/api/demos/${demo.clientName}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...values,
            businessId: values.businessId || null,
            clearCredentialOverride,
            ...(password ? { password } : {}),
            ...(layerClientId || layerClientSecret
              ? { layerClientId, layerClientSecret }
              : {}),
          }),
        })

        if (response.ok) {
          alert(`Updated ${demo.clientName}`)
          await onSaved()
          onCancel()
        } else {
          const errorData = await response.json()
          alert(`Failed to update demo: ${errorData.message || 'Unknown error'}`)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update demo:', error)
        alert('Failed to update demo due to network error')
      }
    },
    [
      authFetch,
      clearCredentialOverride,
      demo.clientName,
      layerClientId,
      layerClientSecret,
      onCancel,
      onSaved,
      password,
      values,
    ],
  )

  return (
    <div className='create-demo-form'>
      <div className='create-demo-form__header create-demo-form__header--with-subtitle'>
        <div>
          <h2>Edit Demo</h2>
          <p>{demo.clientName}</p>
        </div>
        <button className='modal-close-button' onClick={onCancel}>
          &times;
        </button>
      </div>
      <form onSubmit={handleSubmit} className='demo-form'>
        <DemoFormFields values={values} onChange={handleChange} />
        <LabeledField label='Replace Password:'>
          <input
            type='password'
            autoComplete='new-password'
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        </LabeledField>
        <LabeledField label='Replace Layer Client ID:'>
          <input
            type='text'
            value={layerClientId}
            onChange={e => setLayerClientId(e.target.value)}
          />
        </LabeledField>
        <LabeledField label='Replace Layer Client Secret:'>
          <input
            type='password'
            value={layerClientSecret}
            onChange={e => setLayerClientSecret(e.target.value)}
          />
        </LabeledField>
        <LabeledField label='Clear credential override:'>
          <input
            type='checkbox'
            checked={clearCredentialOverride}
            onChange={e => setClearCredentialOverride(e.target.checked)}
          />
        </LabeledField>
        <div className='demo-form__actions'>
          <button className='admin-button' type='button' onClick={onCancel}>
            Cancel
          </button>
          <button className='admin-button' type='submit'>
            Save Demo
          </button>
        </div>
      </form>
    </div>
  )
})
